import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { CallerService } from '../../services/caller.service';
import { LearnMoreDialogComponent, LearnMoreDialogData } from './learn-more-dialog.component';
import { LEARN_MORE, LearnMoreTopic, PublicStats } from './learn-more.content';

/**
 * Opens the home page's "Learn more" (a bottom sheet on a phone, a dialog elsewhere) and keeps the URL in step:
 * `/?learn=<topic>` while it's open, so a link (an ad, a WhatsApp share) can open it.
 */
@Injectable({ providedIn: 'root' })
export class LearnMoreService {
  static readonly QUERY_PARAM = 'learn';

  /** Loaded once, when a dialog first asks. A failure (or a backend without /stats yet) just means no numbers. */
  readonly stats$: Observable<PublicStats | null> = this.callerService.getPublicStats().pipe(
    map((res) => res.body?.data ?? null),
    catchError(() => of(null)),
    shareReplay(1),
  );

  constructor(
    private dialog: MatDialog,
    private bottomSheet: MatBottomSheet,
    private callerService: CallerService,
    private router: Router,
    private location: Location,
  ) {}

  open(topic: LearnMoreTopic): void {
    this.setUrlTopic(topic);
    const data: LearnMoreDialogData = { topic, stats$: this.stats$, topicChanged: (next) => this.setUrlTopic(next) };
    const closed$ = this.callerService.isSmallScreen()
      ? this.bottomSheet
          .open(LearnMoreDialogComponent, { data, panelClass: 'learn-more-sheet', ariaLabel: LEARN_MORE[topic].title })
          .afterDismissed()
      : this.dialog
          .open(LearnMoreDialogComponent, {
            data,
            width: '100%',
            maxWidth: '640px',
            maxHeight: '90dvh',
            panelClass: 'learn-more-panel',
            ariaLabelledBy: LearnMoreDialogComponent.TITLE_ID,
            autoFocus: 'dialog',
          })
          .afterClosed();
    closed$.subscribe(() => this.setUrlTopic(null));
  }

  /**
   * Only the address bar, not a router navigation: the router would scroll the page to the top
   * (scrollPositionRestoration) behind the dialog.
   */
  setUrlTopic(topic: LearnMoreTopic | null): void {
    const tree = this.router.parseUrl(this.location.path(true));
    if (topic) {
      tree.queryParams = { ...tree.queryParams, [LearnMoreService.QUERY_PARAM]: topic };
    } else {
      tree.queryParams = { ...tree.queryParams };
      delete tree.queryParams[LearnMoreService.QUERY_PARAM];
    }
    this.location.replaceState(this.router.serializeUrl(tree));
  }
}
